/* eslint-disable arrow-body-style */
import { useMemo, useState } from 'react';
import { history } from 'umi';
import moment from 'moment';
import { useMemoizedFn } from 'ahooks';
import { getLocationIsValid } from '@gp/utils/common';
import { getKeyMilestones } from '@gp/pages/Pipeline/ProjectDetails/Milestone/utils';
import { SLASH_G_POP } from '@gp/constants/const';

export interface IMilestoneNode {
  label: string;
  content: string;
  [key: string]: any;
}

export default function useMilestonesTimelines(props: {
  activeId?: string;
  detailInfo?: Record<string, any>;
  showVisible?: boolean;
  timeVisible?: boolean;
  milestoneData?: {
    update_time?: string;
    list?: any[];
  };
  setActiveId?: React.Dispatch<React.SetStateAction<string>>;
  milestoneLegendList?: any;
  milestoneCategory?: any;
}) {
  const {
    activeId,
    detailInfo,
    showVisible,
    timeVisible,
    milestoneData,
    setActiveId,
    milestoneCategory,
  } = props;

  const isMilestonePage = activeId === 'milestone';

  const milestones = useMemo<IMilestoneNode[]>(() => {
    const { list } = milestoneData || {};

    if (!Array.isArray(list) || !list.length) return [];

    return list?.map((item, index) => {
      const {
        milestone_id: milestoneId,
        date: milestoneDate,
        milestone: milestoneName,
      } = item || {};

      const milestoneDateTrim = milestoneDate?.trim();
      const milestoneDateM: moment.Moment | null = !!milestoneDateTrim
        ? moment(milestoneDateTrim)
        : null;
      const milestoneDateFormatted = milestoneDateM?.isValid()
        ? milestoneDateM.format('YYYY-MM')
        : milestoneDateTrim || '';

      let categoryTemp = '';
      if (item.category) {
        categoryTemp = item.category;
      } else {
        categoryTemp = milestoneCategory?.[milestoneName] || 'others';
      }
      return {
        ...item,
        id: milestoneId,
        label: milestoneDateFormatted,
        content: milestoneName || '',
        category: categoryTemp,
        indexMy: index,
      };
    });
  }, [milestoneData]);

  const { currentMilestoneIdx, nextMilestoneIdx, nextMilestone } = useMemo(() => {
    if (!Array.isArray(milestones) || !milestones.length)
      return {
        currentMilestone: null,
        currentMilestoneIdx: -1,
        nextMilestone: null,
        nextMilestoneIdx: -1,
      };
    const { currentMilestone, nextMilestone } = getKeyMilestones(
      milestones as any,
      detailInfo?.current_milestone_id,
      detailInfo?.next_milestone_id,
    );
    const currentMilestoneIdx = milestones?.findIndex(
      (item) => item?.milestone_id === currentMilestone?.milestone_id,
    );
    const nextMilestoneIdx = milestones?.findIndex(
      (item) => item?.milestone_id === nextMilestone?.milestone_id,
    );
    return { currentMilestone, currentMilestoneIdx, nextMilestone, nextMilestoneIdx };
  }, [milestones, detailInfo?.current_milestone_id, detailInfo?.next_milestone_id]);

  const nextMilestoneText = useMemo<string>(() => {
    const { date: milestoneDate, milestone: milestoneName } = nextMilestone || {};
    const milestoneDateTrim = milestoneDate?.trim();
    const milestoneDateM: moment.Moment | null = !!milestoneDateTrim
      ? moment(milestoneDateTrim)
      : null;
    const milestoneDateFormatted = milestoneDateM?.isValid()
      ? milestoneDateM.format('YYYY-MM')
      : '';
    return `${milestoneName || '-'} (${milestoneDateFormatted || '-'})`;
  }, [nextMilestone]);

  const timelineUpdateDependencies = useMemo<React.DependencyList>(() => {
    return [showVisible, timeVisible, milestones];
  }, [showVisible, timeVisible, milestones]);

  // const [timelineOffsetDistances, setTimelineOffsetDistances] = useState(20);
  // 重构给的数值是35.5
  const [timelineOffsetDistances, setTimelineOffsetDistances] = useState(35.5);

  const getTimelineOffsetMsg = useMemoizedFn((v: any) => {
    setTimelineOffsetDistances(v?.left?.offset - v?.right?.offset);
  });

  const handleGoMilestone = () => {
    if (isMilestonePage) return;
    const filterObj = getLocationIsValid('/Pipeline/ProjectList');
    const filter: Record<string, any> = { ...filterObj };
    const milestoneTabId = 'milestone';
    filter.tab = milestoneTabId;
    // tab切换时，删除详情页带的milestone
    if (filter?.projectUpdateInitMilestone) {
      delete filter.projectUpdateInitMilestone;
    }
    const newFilter = encodeURIComponent(JSON.stringify(filter));
    history.replace(`${SLASH_G_POP}/Pipeline/ProjectList/Detail?filter=${newFilter}`);
    setActiveId?.(milestoneTabId);
  };

  return {
    isMilestonePage,
    isSame: currentMilestoneIdx === nextMilestoneIdx,
    nextMilestoneText,
    timelineStep: currentMilestoneIdx,
    timelines: milestones,
    timelineOffsetDistances,
    timelineUpdateDependencies,
    getTimelineOffsetMsg,
    handleGoMilestone,
  };
}
